import React, { useMemo, useState } from 'react';
import { freedomInitialFeeWarning, freedomPeriodWarning } from '../../../constants/bankConditions';
import styles from './deal.module.css';
import SelectCreditPeriod, { PERIODS } from '../../../components/Selects/SelectCreditPeriod';
import createNumberMask from '../../../utils/createNumberMask';
import HookForm from '../../../components/HookForm/HookForm';
import SelectFamilyStatus, { MARITAL_STATUSES } from '../../../components/Selects/SelectFamilyStatus';
import DealCreateDocs from './DealCreateDocs';
import {
  CLIENT_CONTACT_PERSON_TYPES,
  CLIENT_DOCUMENT_ISSUED_BY,
  DOCUMENT_TYPES_PERSONAL_DATA_AGREEMENT,
  IS_PROD_STAND,
  MARITAL_STATUS_MARRIED,
  RESIDENT_TYPES,
  USED_CAR_BANK_IDS,
} from '../../../constants';
import Modal from '../../../components/Modal/Modal';
import Button from '../../../components/Button/Button';
import DealCreateAgreement from './DealCreateAgreement';
import { MASK } from '../../../constants/mask';
import DocsInputNew from '../../../components/Inputs/DocsInput/DocsInputNew';
import alert from '../../../helpers/alert';
import { textStyles } from '../../../constants/textStyles';
import Hint from '../../../components/Hint';
import numberMask from '../../../utils/numberMask';
import Checkbox from '../../../components/Inputs/Checkbox/Checkbox';
import SelectContactType from '../../../components/Selects/SelectContactType';
import SelectResitentType from '../../../components/Selects/SelectResitentType';
import SelectClientDocumentType from '../../../components/Selects/SelectClientDocumentTypes';
import SelectClientIssuer from '../../../components/Selects/SelectClientDocumentType';
import SelectWorkStatus, { WORK_STATUSES } from '../../../components/Selects/SelectWorkStatus';
import BanksPicker from '../../../components/Credit/BanksPicker';
import Select from '../../../components/Select/Select';
import { shouldClearDraft } from '../../../utils/appealSubmitState';
import getClients from '../../../api/clients/getClients';
import { flagFromDraft, repaymentPayload, withoutRepaymentFields } from './greyAppeal';
import { shortFormsEnabled } from '../../../brand/tenant';
import { loadClientPhoto, loadIdentityScans } from './identityScans';

const CREDIT_TYPE_OPTIONS = [
  {
    label: 'AÐ½Ð½ÑÐ¸ÑÐµÑÐ½ÑÐ¹',
    value: 0,
  },
  {
    label: 'ÐÐ¸ÑÑÐµÑÐµÐ½ÑÐ¸ÑÐ¾Ð²Ð°Ð½Ð½ÑÐ¹',
    value: 1,
  },
];

const DRAFT_STORAGE_PREFIX = 'dealCreateCreditAppealDraft';

const isBrowserStorageAvailable = () => typeof window !== 'undefined' && !!window.localStorage;

const readDraft = (key) => {
  if (!key || !isBrowserStorageAvailable()) return {};

  try {
    return JSON.parse(window.localStorage.getItem(key)) || {};
  } catch (e) {
    return {};
  }
};

const writeDraft = (key, draft) => {
  if (!key || !isBrowserStorageAvailable()) return;

  try {
    window.localStorage.setItem(key, JSON.stringify(draft));
  } catch (e) {}
};

const removeDraft = (key) => {
  if (!key || !isBrowserStorageAvailable()) return;
  window.localStorage.removeItem(key);
};

const findOption = (options, value) => {
  if (value && typeof value === 'object' && 'value' in value) return value;
  return options.find((option) => String(option.value) === String(value));
};

const normalizeFormValues = (values = {}) => {
  const normalized = { ...values };

  normalized.repaymentType = findOption(CREDIT_TYPE_OPTIONS, values.repaymentType);
  normalized.period = findOption(PERIODS, values.period);
  normalized.MaritalStatus = findOption(MARITAL_STATUSES, values.MaritalStatus);
  normalized.workStatus = findOption(WORK_STATUSES, values.workStatus?.value || values.workStatusId || values.workStatus);
  normalized.contactPersonType = findOption(CLIENT_CONTACT_PERSON_TYPES, values.contactPersonType);
  normalized.contactPersonType2 = findOption(CLIENT_CONTACT_PERSON_TYPES, values.contactPersonType2);
  normalized.residentBool = findOption(RESIDENT_TYPES, values.residentBool);
  normalized.residentBool2 = findOption(RESIDENT_TYPES, values.residentBool2);
  normalized.documentGivedBy = findOption(CLIENT_DOCUMENT_ISSUED_BY, values.documentGivedBy);
  normalized.documentGivedBy2 = findOption(CLIENT_DOCUMENT_ISSUED_BY, values.documentGivedBy2);

  return normalized;
};

const DealCreateCreditAppeal = ({
  deal,
  documents,
  onSubmit,
  orders,
  agreementModal,
  setAgreementModal,
  backHandler,
  sendFormLoading,
  defaultValues,
  udvIsRequired = true,
  usedCar = false,
}) => {
  const draftStorageKey = React.useMemo(() => {
    const dealKey = deal?.chunkId || deal?.id;
    return dealKey ? `${DRAFT_STORAGE_PREFIX}:${dealKey}` : null;
  }, [deal?.chunkId, deal?.id]);
  const savedDraft = React.useMemo(() => readDraft(draftStorageKey), [draftStorageKey]);
  const restoredValues = React.useMemo(
    () => normalizeFormValues({ ...(defaultValues || {}), ...(savedDraft?.values || {}) }),
    [defaultValues, savedDraft]
  );
  const saveDraftTimeoutRef = React.useRef(null);
  const latestValuesRef = React.useRef(restoredValues);

  // ÐÐµÐ· Ð´Ð°Ð½Ð½ÑÑ ÑÐ´Ð¾ÑÑÐ¾Ð²ÐµÑÑÑÑÐµÐ³Ð¾ Ð´Ð¾ÐºÑÐ¼ÐµÐ½ÑÐ° Ð·Ð°ÑÐ²ÐºÑ Ð¾ÑÐ¿ÑÐ°Ð²Ð»ÑÑÑ Ð½ÐµÐºÑÐ´Ð°: Ð¨Ð¸Ð½ÑÐ°Ð½ Ð½Ð°
  // ÑÐ°ÐºÐ¸Ñ ÐºÐ»Ð¸ÐµÐ½ÑÐ°Ñ Ð¿Ð°Ð´Ð°Ð» (ÑÐ´ÐµÐ»ÐºÐ¸ 189083, 189221, 189240), Ð¾ÑÑÐ°Ð»ÑÐ½ÑÐµ Ð±Ð°Ð½ÐºÐ¸
  // Ð¾ÑÐºÐ°Ð·ÑÐ²Ð°ÑÑ. ÐÐ°ÑÑÐ¾ÑÐºÑ ÐºÐ»Ð¸ÐµÐ½ÑÐ° Ð¼Ð¾Ð¶Ð½Ð¾ Ð·Ð°Ð²ÐµÑÑÐ¸ Ð¸ Ð±ÐµÐ· Ð´Ð¾ÐºÑÐ¼ÐµÐ½ÑÐ° â Ð½Ð°Ð¿ÑÐ¸Ð¼ÐµÑ,
  // Ð¸Ð· Ð¼Ð¾Ð±Ð¸Ð»ÑÐ½Ð¾Ð³Ð¾ Ð¿ÑÐ¸Ð»Ð¾Ð¶ÐµÐ½Ð¸Ñ, â Ð¿Ð¾ÑÑÐ¾Ð¼Ñ Ð¿ÑÐ¾Ð²ÐµÑÑÐµÐ¼ Ð¿ÐµÑÐµÐ´ Ð¾ÑÐ¿ÑÐ°Ð²ÐºÐ¾Ð¹.
  const [clientDocumentMissing, setClientDocumentMissing] = React.useState(false);
  const [clientPhotoUrl, setClientPhotoUrl] = React.useState('');

  React.useEffect(() => {
    const clientId = deal?.client?.id;
    if (!clientId) return;

    getClients({ id: clientId })
      .then((res) => {
        const client = res?.data?.users?.[0];
        setClientDocumentMissing(!client?.document?.document_number);
        setClientPhotoUrl(client?.photo || '');
      })
      .catch(() => setClientDocumentMissing(false));
  }, [deal?.client?.id]);

  const [files, setFiles] = React.useState({
    UDV: null,
    passportRK: null,
    clientPhoto: null,
    incomeCertificate: null,
    workCertificate: null,
    legalConfirm: null,
    vinApprovalDocument: null,
  });

  // Ð¡ÐºÐ°Ð½ ÑÐ´Ð¾ÑÑÐ¾Ð²ÐµÑÐµÐ½Ð¸Ñ ÐºÐ»Ð¸ÐµÐ½ÑÐ° ÑÐ¶Ðµ Ð¼Ð¾Ð¶ÐµÑ Ð»ÐµÐ¶Ð°ÑÑ Ð² ÐµÐ³Ð¾ ÐºÐ°ÑÑÐ¾ÑÐºÐµ (ÐµÐ³Ð¾ ÐºÐ»Ð°Ð´ÑÑ ÐÐÐ Ð¿ÑÐ¸ Ð´Ð¾Ð±Ð°Ð²Ð»ÐµÐ½Ð¸Ð¸
  // ÑÐµÑÐµÐ· eGov) â Ð¿Ð¾Ð´ÑÑÐ°Ð²Ð»ÑÐµÐ¼ Ð² Â«Ð£ÐÐÂ» Ð¸ Â«ÐÐ°ÑÐ¿Ð¾ÑÑ Ð ÐÂ», ÑÑÐ¾Ð±Ñ Ð½Ðµ Ð³ÑÑÐ·Ð¸ÑÑ Ð·Ð°Ð½Ð¾Ð²Ð¾. Ð ÑÐºÐ°Ð¼Ð¸ Ð²ÑÐ±ÑÐ°Ð½Ð½Ð¾Ðµ Ð½Ðµ ÑÑÐ¾Ð³Ð°ÐµÐ¼.
  React.useEffect(() => {
    const clientId = deal?.client?.id;
    if (!clientId) return undefined;
    let cancelled = false;
    loadIdentityScans(clientId)
      .then((found) => {
        if (cancelled) return;
        setFiles((prev) => ({
          ...prev,
          UDV: prev.UDV || found.UDV || null,
          passportRK: prev.passportRK || found.passportRK || null,
        }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [deal?.client?.id]);

  // Ð¤Ð¾ÑÐ¾Ð³ÑÐ°ÑÐ¸Ñ ÐºÐ»Ð¸ÐµÐ½ÑÐ° Ð»ÐµÐ¶Ð¸Ñ Ð½Ðµ Ð² Ð´Ð¾ÐºÑÐ¼ÐµÐ½ÑÐ°Ñ, Ð° Ð² Ð°Ð²Ð°ÑÐ°ÑÐµ ÐºÐ°ÑÑÐ¾ÑÐºÐ¸ â ÐµÑ ÑÐ¾Ð¶Ðµ ÐºÐ»Ð°Ð´ÑÑ
  // ÐÐÐ (ÑÐ½Ð¸Ð¼Ð¾Ðº Ñ ÑÐ´Ð¾ÑÑÐ¾Ð²ÐµÑÐµÐ½Ð¸Ñ). ÐÐ°Ð½ÐºÐ¸ ÑÑÐµÐ±ÑÑÑ ÑÐ¾ÑÐ¾ Ð² Ð°Ð½ÐºÐµÑÐµ, Ð¸ Ð¼ÐµÐ½ÐµÐ´Ð¶ÐµÑ Ð¸ÑÐºÐ°Ð» ÐµÐ³Ð¾
  // ÑÐ°Ð¼, ÑÐ¾ÑÑ Ð¾Ð½Ð¾ ÑÐ¶Ðµ ÐµÑÑÑ.
  React.useEffect(() => {
    if (!clientPhotoUrl) return undefined;
    let cancelled = false;
    loadClientPhoto(clientPhotoUrl).then((photo) => {
      if (cancelled || !photo) return;
      setFiles((prev) => ({ ...prev, clientPhoto: prev.clientPhoto || photo }));
    });
    return () => {
      cancelled = true;
    };
  }, [clientPhotoUrl]);

  // const inputs = useMemo(() => {
  const inputs = [
    {
      label: 'ÐÐ¸Ð´ Ð¿Ð»Ð°ÑÐµÐ¶Ð°',
      name: 'repaymentType',
      component: Select,
      options: CREDIT_TYPE_OPTIONS,
      rules: {
        required: true,
      },
      placeholder: 'ÐÑÐ±ÐµÑÐ¸ÑÐµ Ð¸Ð· ÑÐ¿Ð¸ÑÐºÐ°',
    },
    {
      label: 'ÐÐµÐ½Ñ Ð¿Ð¾Ð³Ð°ÑÐµÐ½Ð¸Ñ',
      name: 'repaymentDay',
      placeholder: 'ÐÑ 1 Ð´Ð¾ 23',
      inputType: 'number',
      rules: {
        validate: (value) => {
          if (value === undefined || value === null || value === '') return true;
          const num = Number(value);
          if (Number.isNaN(num) || num < 1 || num > 23) {
            return 'ÐÐ²ÐµÐ´Ð¸ÑÐµ ÑÐ¸ÑÐ»Ð¾ Ð¾Ñ 1 Ð´Ð¾ 23';
          }
          return true;
        },
      },
    },
    {
      label: 'ÐÐµÑÐ²Ð¾Ð½Ð°ÑÐ°Ð»ÑÐ½ÑÐ¹ Ð²Ð·Ð½Ð¾Ñ',
      name: 'initialFee',
      placeholder: 'ÐÐ²ÐµÐ´Ð¸ÑÐµ Ð¿ÐµÑÐ²Ð¾Ð½Ð°ÑÐ°Ð»ÑÐ½ÑÐ¹ Ð²Ð·Ð½Ð¾Ñ',
      mask: numberMask,
      inputMode: 'numeric',
      hint: 'ÐÐ»Ñ Ð¿Ð¾Ð»ÑÑÐµÐ½Ð¸Ñ Ð¿Ð¾Ð»Ð¾Ð¶Ð¸ÑÐµÐ»ÑÐ½Ð¾Ð³Ð¾ Ð¾ÑÐ²ÐµÑÐ° Ð¸ Ð²ÑÐ³Ð¾Ð´Ð½ÑÑ ÑÑÐ»Ð¾Ð²Ð¸Ð¹ Ð¾Ñ ForteBank ÑÑÐµÐ±ÑÐµÑÑÑ Ð¿ÐµÑÐ²Ð¾Ð½Ð°ÑÐ°Ð»ÑÐ½ÑÐ¹ Ð²Ð·Ð½Ð¾Ñ Ð½Ðµ Ð¼ÐµÐ½ÐµÐµ 15%.',
      // Freedom Ð¾ÑÐºÐ°Ð·ÑÐ²Ð°ÐµÑ Ð¿Ð¾ Ð²Ð·Ð½Ð¾ÑÑ ÑÐ°ÑÐµ, ÑÐµÐ¼ Ð¾Ð´Ð¾Ð±ÑÑÐµÑ Ð·Ð°ÑÐ²ÐºÐ¸: Ð·Ð° Ð½ÐµÐ´ÐµÐ»Ñ 203
      // Ð¾ÑÐºÐ°Ð·Ð° Ð¸Ð· 937 Ð·Ð°ÑÐ²Ð¾Ðº, Ð¿Ð¾ÑÑÐ¸ Ð²ÑÐµ â Ð²Ð·Ð½Ð¾Ñ Ð½Ð¸Ð¶Ðµ 10%, Ð¾Ð±ÑÑÐ½Ð¾ Ð½ÑÐ»ÐµÐ²Ð¾Ð¹.
      // ÐÐµÐ½ÐµÐ´Ð¶ÐµÑ ÑÐ·Ð½Ð°Ð²Ð°Ð» Ð¾Ð± ÑÑÐ¾Ð¼ ÑÐµÑÐµÐ· Ð¿Ð¾Ð»ÑÐ°ÑÐ° Ð¸Ð· ÐºÐ°ÑÑÐ¾ÑÐºÐ¸.
      warning: (values) => freedomInitialFeeWarning(values?.initialFee, deal?.sum),
      rules: {
        valueAsNumber: true,
        validate: (value) => {
          value = value?.replace(/\s/g, '');
          value = parseInt(value);
          console.log(deal?.sum);
          let error = true;
          if (value === 0) {
            return error;
          }
          const carPrice = deal?.sum;
          // if (value < (carPrice * 0.1)?.toFixed(0)) {
          //   error = `ÐÐ¸Ð½Ð¸Ð¼Ð°Ð»ÑÐ½Ð°Ñ ÑÑÐ¼Ð¼Ð° Ð²Ð·Ð½Ð¾ÑÐ° 10% Ð¾Ñ ÑÑÐ¾Ð¸Ð¼Ð¾ÑÑÐ¸ Ð°Ð²ÑÐ¾Ð¼Ð¾Ð±Ð¸Ð»Ñ`;
          // } else
          if (value > carPrice) {
            error = 'Ð¡ÑÐ¼Ð¼Ð° Ð²Ð·Ð½Ð¾ÑÐ° Ð½Ðµ Ð¼Ð¾Ð¶ÐµÑ Ð¿ÑÐµÐ²ÑÑÐ°ÑÑ ÑÑÐ¾Ð¸Ð¼Ð¾ÑÑÑ Ð°Ð²ÑÐ¾Ð¼Ð¾Ð±Ð¸Ð»Ñ';
          } else {
            error = true;
          }

          return error;
        },
      },
      // defaultValue: defaultValues?.initialFee,
    },
    {
      label: 'Ð¡ÑÐ¾Ðº ÐºÑÐµÐ´Ð¸ÑÐ¾Ð²Ð°Ð½Ð¸Ñ, Ð¼ÐµÑÑÑÐµÐ²',
      name: 'period',
      component: SelectCreditPeriod,
      // ÐÐ° 36, 48 Ð¸ 24 Ð¼ÐµÑÑÑÐ°Ñ Freedom Ð¾ÑÐºÐ°Ð·ÑÐ²Ð°ÐµÑ Ð¿Ð¾ÑÑÐ¸ Ð²ÑÐµÐ³Ð´Ð° (45 Ð¸Ð· 48,
      // 37 Ð¸Ð· 40, 19 Ð¸Ð· 23 Ð·Ð°ÑÐ²Ð¾Ðº), Ð½Ð° 60/72/84 Ð¾ÑÐºÐ°Ð·Ð¾Ð² Ð¿Ð¾ ÑÑÐ¾ÐºÑ Ð½ÐµÑ Ð²Ð¾Ð²ÑÐµ.
      warning: (values) => freedomPeriodWarning(values?.period),
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.period,
    },
    {
      label: 'ÐÑÐ½Ð¾Ð²Ð½Ð¾Ð¹ Ð´Ð¾ÑÐ¾Ð´',
      name: 'incomeMain',
      rules: {
        required: true,
      },
      mask: numberMask,
      inputMode: 'numeric',
      // defaultValue: defaultValues?.incomeMain,
    },
    // {
    //   label: 'ÐÐ¾Ð¿Ð¾Ð»Ð½Ð¸ÑÐµÐ»ÑÐ½ÑÐ¹ Ð·Ð°ÑÐ°Ð±Ð¾ÑÐ¾Ðº',
    //   name: 'IncomeAddConfirmed',
    //   mask: numberMask,
    // },
    {
      label: 'ÐÐµÑÑÐ¾ ÑÐ°Ð±Ð¾ÑÑ',
      name: 'workName',
      rules: {
        required: true,
        validate: (value) => {
          if (value.length > 50) {
            return 'ÐÐ°ÐºÑÐ¸Ð¼Ð°Ð»ÑÐ½Ð°Ñ Ð´Ð¾Ð¿ÑÑÑÐ¸Ð¼Ð°Ñ Ð´Ð»Ð¸Ð½Ð° Ð¿Ð¾Ð»Ñ "ÐÐµÑÑÐ¾ ÑÐ°Ð±Ð¾ÑÑ" - 50 ÑÐ¸Ð¼Ð²Ð¾Ð»Ð¾Ð²';
          }

          return true;
        },
      },
      // defaultValue: defaultValues?.workName,
    },
    {
      label: 'ÐÐ´ÑÐµÑ ÑÐ°Ð±Ð¾ÑÑ',
      name: 'workAddress',
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.workAddress,
    },
    {
      label: 'ÐÐ¾Ð»Ð¶Ð½Ð¾ÑÑÑ',
      name: 'jobTitle',
      rules: {
        required: true,
      },
    },
    // Ð¢ÐµÐ»ÐµÑÐ¾Ð½ ÐºÐ¾Ð¼Ð¿Ð°Ð½Ð¸Ð¸ ÑÑÐ¾Ð¸Ñ ÑÑÐ´Ð¾Ð¼ Ñ Ð¼ÐµÑÑÐ¾Ð¼ ÑÐ°Ð±Ð¾ÑÑ: Ð²Ð½Ð¸Ð·Ñ ÑÐ¾ÑÐ¼Ñ, Ð¿Ð¾ÑÐ»Ðµ Â«ÐÐ¾Ð»Ð¸ÑÐµÑÑÐ²Ð¾
    // Ð´ÐµÑÐµÐ¹Â», Ð¼ÐµÐ½ÐµÐ´Ð¶ÐµÑÑ ÐµÐ³Ð¾ Ð¿ÑÐ¾Ð¿ÑÑÐºÐ°Ð»Ð¸ â Ð¿Ð¾Ð»Ðµ Ð¾Ð±ÑÐ·Ð°ÑÐµÐ»ÑÐ½Ð¾Ðµ, Ð¸ Ð·Ð°ÑÐ²ÐºÐ° Ð½Ðµ Ð¾ÑÐ¿ÑÐ°Ð²Ð»ÑÐ»Ð°ÑÑ.
    {
      label: 'ÐÐ¾Ð¼ÐµÑ ÑÐµÐ»ÐµÑÐ¾Ð½Ð° ÐºÐ¾Ð¼Ð¿Ð°Ð½Ð¸Ð¸, Ð² ÐºÐ¾ÑÐ¾ÑÐ¾Ð¹ ÑÐ°Ð±Ð¾ÑÐ°ÐµÑÐµ',
      name: 'companyPhone',

      type: 'phone',
      mask: MASK.PHONE,
      inputMode: 'numeric',

      hint: 'ÐÐ¾Ð¼ÐµÑ ÑÐµÐ»ÐµÑÐ¾Ð½Ð° ÐºÐ¾Ð¼Ð¿Ð°Ð½Ð¸Ð¸ Ð½ÑÐ¶ÐµÐ½ ÑÐ¾Ð»ÑÐºÐ¾ Ð´Ð»Ñ ÐÐ²ÑÐ°Ð·Ð¸Ð¹ÑÐºÐ¾Ð³Ð¾ Ð±Ð°Ð½ÐºÐ°',
      rules: {
        required: true,
        validate: (value) => {
          value = value.replace(/[^0-9.]/g, '');
          return value.length === 11;
        },
      },
      // defaultValue: defaultValues?.companyPhone,
    },
    {
      component: SelectWorkStatus,
      name: 'workStatus',
      hint: 'ÐÐ±ÑÐ·Ð°ÑÐµÐ»ÑÐ½Ð¾ Ð´Ð»Ñ ÐÐ°Ð½Ðº Ð¦ÐµÐ½ÑÑÐÑÐµÐ´Ð¸Ñ',
      // defaultValue: defaultValues?.workStatus,
    },
    {
      label: 'Ð¡ÐµÐ¼ÐµÐ¹Ð½Ð¾Ðµ Ð¿Ð¾Ð»Ð¾Ð¶ÐµÐ½Ð¸Ðµ',
      name: 'MaritalStatus',
      component: SelectFamilyStatus,
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.MaritalStatus,
    },
    {
      label: 'ÐÐ¾ÑÐ¾Ð´ ÑÑÐ¿ÑÑÐ³Ð¸/ÑÑÐ¿ÑÑÐ³Ð°',
      name: 'SpouseIncomeAmount',
      mask: numberMask,
      inputMode: 'numeric',
      hide: (values) => !(values === MARITAL_STATUS_MARRIED),
      dependency: `MaritalStatus`,
      dependencyAccessor: (obj) => obj?.value,
      // defaultValue: defaultValues?.SpouseIncomeAmount,
    },
    {
      label: 'ÐÐ¾Ð»Ð¸ÑÐµÑÑÐ²Ð¾ Ð´ÐµÑÐµÐ¹',
      name: 'childrensCount',
      mask: numberMask,
      inputMode: 'numeric',
      // defaultValue: defaultValues?.childrensCount,
      rules: {
        required: true,
      },
    },
    // {
    //   label: 'Ð¡ÑÑÐ°ÑÐ¾Ð²ÐºÐ° Ð´Ð»Ñ Sapa',
    //   name: 'kaskoInsuranceRate',
    //   component: Select,
    //   options: [
    //     { label: 'KASKO 2.47', value: '1' },
    //     { label: 'KASKO 4.84', value: '2' },
    //     { label: 'ÐÐµÑ', value: null },
    //   ],
    //   rules: {
    //     required: true,
    //   },
    //   // defaultValue: defaultValues?.kaskoInsuranceRate,
    // },
    // {
    //   label: 'Ð¡ÑÑÐ°ÑÐ¾Ð²Ð°Ð½Ð¸Ðµ Ð¶Ð¸Ð·Ð½Ð¸ Ð´Ð»Ñ Sapa',
    //   name: 'lifeInsuranceRate',
    //   component: Select,
    //   options: [
    //     { label: 'ÐÐ°ÐºÑÐ¸Ð¼ÑÐ¼ 8', value: '1' },
    //     { label: 'Pro 5.5', value: '3' },
    //     { label: 'Start 4.87', value: '4' },
    //     { label: 'Ð¡ÑÐ±ÑÐ¸Ð´Ð¸Ñ 6', value: '6' },
    //     { label: 'ÐÐµÑ', value: null },
    //   ],
    //   rules: {
    //     required: true,
    //   },
    //   // defaultValue: defaultValues?.kaskoInsuranceRate,
    // },
    {
      component: () => {
        return <p style={{ ...textStyles.formTitle, marginTop: 20 }}>ÐÐ¾Ð½ÑÐ°ÐºÑÐ½Ð¾Ðµ Ð»Ð¸ÑÐ¾</p>;
      },
      name: 'ÐÐ¾Ð½ÑÐ°ÐºÑÐ½Ð¾Ðµ Ð»Ð¸ÑÐ¾',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
    },
    {
      label: 'Ð¤Ð°Ð¼Ð¸Ð»Ð¸Ñ',
      name: 'contactPersonLastName',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.contactPersons?.[0]?.contactPersonLastName,
    },
    {
      label: 'ÐÐ¼Ñ',
      name: 'contactPersonFirstName',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.contactPersons?.[0]?.contactPersonFirstName,
    },
    {
      label: 'ÐÐ¾Ð¼ÐµÑ ÑÐµÐ»ÐµÑÐ¾Ð½Ð°',
      name: 'contactPersonPhone',
      type: 'phone',
      mask: MASK.PHONE,
      inputMode: 'numeric',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
      rules: {
        required: true,
        validate: (value) => {
          value = value.replace(/[^0-9.]/g, '');
          return value.length === 11;
        },
      },
      // defaultValue: defaultValues?.contactPersons?.[0]?.contactPersonPhone,
    },
    {
      component: SelectContactType,
      name: 'contactPersonType',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.contactPersons?.[0]?.contactPersonType,
    },
    {
      component: SelectResitentType,
      name: 'residentBool',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.contactPersons?.[0]?.residentBool,
    },
    {
      component: SelectClientIssuer,
      label: 'ÐÑÐ³Ð°Ð½ Ð²ÑÐ´Ð°ÑÐ¸ Ð´Ð¾ÐºÑÐ¼ÐµÐ½ÑÐ°',
      name: 'documentGivedBy',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.contactPersons?.[0]?.documentGivedBy,
    },
    // {
    //   label: 'ÐÐ¾Ð»Ð¶Ð½Ð¾ÑÑÑ',
    //   name: 'jobTitle',
    //   hide: (value) => !value,
    //   dependencyAccessor: (obj) => obj,
    //   dependency: `workStatus`,
    //   rules: {
    //     required: true,
    //   },
    // },

    {
      component: () => {
        return <p style={{ ...textStyles.formTitle, marginTop: 20 }}>ÐÐ¾Ð½ÑÐ°ÐºÑÐ½Ð¾Ðµ Ð»Ð¸ÑÐ¾ 2</p>;
      },
      name: 'ÐÐ¾Ð½ÑÐ°ÐºÑÐ½Ð¾Ðµ Ð»Ð¸ÑÐ¾2',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
    },
    {
      label: 'Ð¤Ð°Ð¼Ð¸Ð»Ð¸Ñ',
      name: 'contactPersonLastName2',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.contactPersons?.[1]?.contactPersonLastName,
    },
    {
      label: 'ÐÐ¼Ñ',
      name: 'contactPersonFirstName2',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.contactPersons?.[1]?.contactPersonFirstName,
    },
    {
      label: 'ÐÐ¾Ð¼ÐµÑ ÑÐµÐ»ÐµÑÐ¾Ð½Ð°',
      name: 'contactPersonPhone2',
      type: 'phone',
      mask: MASK.PHONE,
      inputMode: 'numeric',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.contactPersons?.[1]?.contactPersonPhone,
    },
    {
      component: SelectContactType,
      name: 'contactPersonType2',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.contactPersons?.[1]?.contactPersonType,
    },
    {
      component: SelectResitentType,
      name: 'residentBool2',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.contactPersons?.[1]?.residentBool,
    },
    {
      component: SelectClientIssuer,
      label: 'ÐÑÐ³Ð°Ð½ Ð²ÑÐ´Ð°ÑÐ¸ Ð´Ð¾ÐºÑÐ¼ÐµÐ½ÑÐ°',
      name: 'documentGivedBy2',
      hide: (value) => !value,
      dependencyAccessor: (obj) => obj,
      dependency: `workStatus`,
      rules: {
        required: true,
      },
      // defaultValue: defaultValues?.contactPersons?.[1]?.documentGivedBy,
    },

    // {
    //   label: 'ÐÐ¾Ð»Ð¶Ð½Ð¾ÑÑÑ',
    //   name: 'jobTitle2',
    //   hide: (value) => !value,
    //   dependencyAccessor: (obj) => obj,
    //   dependency: `workStatus`,
    //   rules: {
    //     required: true,
    //   },
    // },
  ];
  // return inputs;
  // }, [defaultValues])

  const attachFile = (file, key) => {
    setFiles((prev) => ({
      ...prev,
      [key]: file,
    }));
  };

  const [documentsForm, setDocumentsForm] = React.useState([]);
  const [subsidy, setSubsidy] = React.useState(flagFromDraft(savedDraft?.meta?.subsidy));
  const [grace, setGrace] = React.useState(flagFromDraft(savedDraft?.meta?.grace));
  // const [incomeConfirmation, setincomeConfirmation] = React.useState(false);
  const [form, setForm] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [selectedBanks, setSelectedBanks] = React.useState(savedDraft?.meta?.selectedBanks);
  const [vehicle_status, setVehicle_status] = React.useState(flagFromDraft(savedDraft?.meta?.vehicle_status));
  const [benefit, setBenefit] = React.useState(flagFromDraft(savedDraft?.meta?.benefit));
  // Ð¢ÐµÑÑÐ¾Ð²ÑÐ¹ Ð¿ÐµÑÐµÐºÐ»ÑÑÐ°ÑÐµÐ»Ñ: Ð¿Ð¾Ð¼ÐµÑÐ°ÐµÑ Ð·Ð°ÑÐ²ÐºÑ ÐºÐ°Ðº Ð¿ÑÐ¸ÑÐµÐ´ÑÑÑ Ð¸Ð· Ð¼Ð¾Ð±Ð¸Ð»ÑÐ½Ð¾Ð³Ð¾ Ð¿ÑÐ¸Ð»Ð¾Ð¶ÐµÐ½Ð¸Ñ
  // (createCreditAppealRequest.app -> credit_applications.isMobileApp). ÐÑÐ¶ÐµÐ½, ÑÑÐ¾Ð±Ñ
  // Ð½Ð° ÑÐµÑÑÐ¾Ð²Ð¾Ð¼ ÑÑÐµÐ½Ð´Ðµ Ð¿ÑÐ¾Ð²ÐµÑÑÑÑ Ð¿ÑÐ°Ð²Ð¸Ð»Ð¾ "2 ÑÐ°ÑÐ° Ð±ÐµÐ· Ð¾ÑÐ²ÐµÑÐ° Ð±Ð°Ð½ÐºÐ°", Ð½Ðµ ÑÐ¾Ð±Ð¸ÑÐ°Ñ APK.
  // ÐÐ° Ð¿ÑÐ¾Ð´Ðµ ÑÐºÑÑÑ (IS_PROD_STAND).
  const [emulateMobileApp, setEmulateMobileApp] = React.useState(false);
  const persistDraft = React.useCallback(
    (values) => {
      if (!draftStorageKey) return;
      latestValuesRef.current = values;

      if (saveDraftTimeoutRef.current) {
        clearTimeout(saveDraftTimeoutRef.current);
      }

      saveDraftTimeoutRef.current = setTimeout(() => {
        writeDraft(draftStorageKey, {
          values,
          meta: {
            subsidy,
            grace,
            selectedBanks,
            vehicle_status,
            benefit,
          },
        });
      }, 300);
    },
    [benefit, draftStorageKey, grace, selectedBanks, subsidy, vehicle_status]
  );

  React.useEffect(() => {
    persistDraft(latestValuesRef.current);

    return () => {
      if (saveDraftTimeoutRef.current) {
        clearTimeout(saveDraftTimeoutRef.current);
      }
    };
  }, [benefit, grace, persistDraft, selectedBanks, subsidy, vehicle_status]);

  const inputsWithDefaultValues = withoutRepaymentFields(inputs).map((input) => ({
    ...input,
    defaultValue: restoredValues[input.name],
  }));

  const handleSubmit = (values) => {
    // if (!files['UDV'] && !files['passportRK']) {
    //   return alert.error('ÐÑÐ¸ÐºÑÐµÐ¿Ð¸ÑÐµ Ð¾Ð´Ð¸Ð½ Ð¸Ð· Ð´Ð¾ÐºÑÐ¼ÐµÐ½ÑÐ¾Ð²: Ð£ÐÐ Ð¸Ð»Ð¸ Ð¿Ð°ÑÐ¿Ð¾ÑÑ Ð Ð');
    // }

    if (!files['UDV'] && udvIsRequired) {
      return alert.error('ÐÑÐ¸ÐºÑÐµÐ¿Ð¸ÑÐµ Ð´Ð¾ÐºÑÐ¼ÐµÐ½Ñ: Ð£ÐÐ');
    }
    if (!files['clientPhoto']) {
      return alert.error('ÐÑÐ¸ÐºÑÐµÐ¿Ð¸ÑÐµ Ð´Ð¾ÐºÑÐ¼ÐµÐ½Ñ: Ð¤Ð¾ÑÐ¾Ð³ÑÐ°ÑÐ¸Ñ ÐºÐ»Ð¸ÐµÐ½ÑÐ°');
    }

    // if (!files['incomeCertificate']) {
    //   return alert.error('ÐÑÐ¸ÐºÑÐµÐ¿Ð¸ÑÐµ ÑÐ¿ÑÐ°Ð²ÐºÑ Ð¾ Ð´Ð¾ÑÐ¾Ð´Ð°Ñ');
    // }
    if (values?.workStatus?.value == '9' && !files['workCertificate']) {
      return alert.error('ÐÐ»Ñ ÑÑÐ°ÑÑÑÐ° ÐºÐ»Ð¸ÐµÐ½ÑÐ° "ÐÐ»Ð¸ÐµÐ½Ñ Ñ Ð²ÑÐ¿Ð¸ÑÐºÐ¾Ð¹ ÐÐÐ£" Ð¾Ð±ÑÐ·Ð°ÑÐµÐ»ÑÐ½ÑÐ¹ Ð´Ð¾ÐºÑÐ¼ÐµÐ½Ñ "ÐÑÐ¿Ð¸ÑÐºÐ° ÑÐ¾ ÑÑÐµÑÐ°"');
    }
    if (['3', '6', '10']?.includes(values?.workStatus?.value) && !files['incomeCertificate']) {
      return alert.error('ÐÐ»Ñ ÑÐµÐºÑÑÐµÐ³Ð¾ ÑÑÐ°ÑÑÑÐ° ÐºÐ»Ð¸ÐµÐ½ÑÐ° Ð¾Ð±ÑÐ·Ð°ÑÐµÐ»ÑÐ½ÑÐ¹ Ð´Ð¾ÐºÑÐ¼ÐµÐ½Ñ "Ð¡Ð¿ÑÐ°Ð²ÐºÐ° Ð¾ Ð´Ð¾ÑÐ¾Ð´Ð°Ñ"');
    }

    if (values?.companyPhone?.replace(/[^\d]/g, '') === deal?.client?.phone?.replace(/[^\d]/g, '')) {
      return alert.error('ÐÐ¾Ð¼ÐµÑ ÑÐµÐ»ÐµÑÐ¾Ð½Ð° ÐºÐ¾Ð¼Ð¿Ð°Ð½Ð¸Ð¸ Ð¸ ÐºÐ»Ð¸ÐµÐ½ÑÐ° ÑÐ¾Ð²Ð¿Ð°Ð´Ð°ÑÑ');
    }

    if (values?.contactPersonPhone?.replace(/[^\d]/g, '') === deal?.client?.phone?.replace(/[^\d]/g, '')) {
      return alert.error('ÐÐ¾Ð¼ÐµÑ ÑÐµÐ»ÐµÑÐ¾Ð½Ð° ÐºÐ¾Ð½ÑÐ°ÐºÑÐ½Ð¾Ð³Ð¾ Ð»Ð¸ÑÐ° Ð¸ ÐºÐ»Ð¸ÐµÐ½ÑÐ° ÑÐ¾Ð²Ð¿Ð°Ð´Ð°ÑÑ');
    }
    if (values?.contactPersonPhone2?.replace(/[^\d]/g, '') === deal?.client?.phone?.replace(/[^\d]/g, '')) {
      return alert.error('ÐÐ¾Ð¼ÐµÑ ÑÐµÐ»ÐµÑÐ¾Ð½Ð° ÐºÐ¾Ð½ÑÐ°ÐºÑÐ½Ð¾Ð³Ð¾ Ð»Ð¸ÑÐ° Ð¸ ÐºÐ»Ð¸ÐµÐ½ÑÐ° ÑÐ¾Ð²Ð¿Ð°Ð´Ð°ÑÑ');
    }
    setLoading(true);
    try {
      const contactPersons = [];

      if (values?.contactPersonLastName) {
        contactPersons?.push({
          contactPersonLastName: values?.contactPersonLastName,
          contactPersonFirstName: values?.contactPersonFirstName,
          contactPersonPhone: values?.contactPersonPhone?.replace(/[^\d]/g, ''),
          contactPersonType: values?.contactPersonType?.value,
          residentBool: values?.residentBool?.value,
          documentGivedBy:
            String(values?.documentGivedBy?.value || values?.documentGivedBy) === '1'
              ? 'MINISTRY_OF_THE_INTERIOR'
              : 'MINISTRY_OF_JUSTICE',
          // jobTitle: values?.jobTitle,
        });
      }
      if (values?.contactPersonLastName2) {
        contactPersons?.push({
          contactPersonLastName: values?.contactPersonLastName2,
          contactPersonFirstName: values?.contactPersonFirstName2,
          contactPersonPhone: values?.contactPersonPhone2?.replace(/[^\d]/g, ''),
          contactPersonType: values?.contactPersonType2?.value,
          residentBool: values?.residentBool2?.value,
          documentGivedBy:
            String(values?.documentGivedBy2?.value || values?.documentGivedBy2) === '1'
              ? 'MINISTRY_OF_THE_INTERIOR'
              : 'MINISTRY_OF_JUSTICE',
          // jobTitle: values?.jobTitle2,
        });
      }

      const payload = {
        ...values,
        ...repaymentPayload(values),
        initialFee: values?.initialFee ? +values?.initialFee?.replace(/\s/g, '') : 0,
        incomeMain: +values?.incomeMain?.replace(/\s/g, ''),
        // IncomeAddConfirmed: +values?.IncomeAddConfirmed?.replace(/\s/g, ''),
        // familyStatus: values?.familyStatus?.value,
        period: +values?.period?.value,
        graceDuration: +values?.period?.value,
        grace,
        // incomeConfirmation: !incomeConfirmation,
        orderIds: orders?.map((item) => item?.value),
        dealId: deal?.id,
        chunkId: deal?.chunkId,
        workStatus: values?.workStatus?.label,
        workStatusId: values?.workStatus?.value,
        subsidy,
        contactPersons: contactPersons,
        bankIds: selectedBanks,
        vehicle_status: vehicle_status ? 3 : 1,
        initialRegistration: vehicle_status,
        childrensCount: values?.childrensCount || 0,
        app: emulateMobileApp,
        // kaskoInsuranceRate: values?.kaskoInsuranceRate?.value,
        // lifeInsuranceRate: values?.lifeInsuranceRate?.value,
        benefit,
      };

      // if (values?.kaskoInsuranceRate?.value) {
      //   payload.kaskoInsuranceRate = values?.kaskoInsuranceRate?.value;
      // } else {
      //   delete payload.kaskoInsuranceRate;
      // }
      // if (values?.lifeInsuranceRate?.value) {
      //   payload.lifeInsuranceRate = values?.lifeInsuranceRate?.value;
      // } else {
      //   delete payload.lifeInsuranceRate;
      // }

      //sapa temp
      if(payload.subsidy) {
        payload.lifeInsuranceRate = '6';
      }

      setForm(payload);
      Promise.resolve(onSubmit(payload, documentsForm, files))
        // Ð§ÐµÑÐ½Ð¾Ð²Ð¸Ðº ÑÑÐ¸ÑÐ°ÐµÐ¼ ÑÐ¾Ð»ÑÐºÐ¾ ÐºÐ¾Ð³Ð´Ð° Ð·Ð°ÑÐ²ÐºÐ° Ð´ÐµÐ¹ÑÑÐ²Ð¸ÑÐµÐ»ÑÐ½Ð¾ ÑÑÐ»Ð° Ð² Ð±Ð°Ð½ÐºÐ¸.
        // Ð Ð°Ð½ÑÑÐµ Ð¾Ð½ ÑÑÐ¸ÑÐ°Ð»ÑÑ Ð½Ð° Ð»ÑÐ±Ð¾Ð¼ Ð¸ÑÑÐ¾Ð´Ðµ â Ð² ÑÐ¾Ð¼ ÑÐ¸ÑÐ»Ðµ Ð½Ð° Ð¿ÐµÑÐ²Ð¾Ð¼ ÐºÐ»Ð¸ÐºÐµ,
        // ÐºÐ¾Ð³Ð´Ð° Ð±ÑÐºÐµÐ½Ð´ Ð¾ÑÐ²ÐµÑÐ°ÐµÑ Â«ÐÐ¢Ð Ð½Ðµ Ð¿Ð¾Ð´Ð¿Ð¸ÑÐ°Ð½Â» Ð¸ Ð¼ÐµÐ½ÐµÐ´Ð¶ÐµÑ Ð¸Ð´ÑÑ Ð¾ÑÐ¿ÑÐ°Ð²Ð»ÑÑÑ
        // ÐºÐ»Ð¸ÐµÐ½ÑÑ ÑÐ¾Ð³Ð»Ð°ÑÐ¸Ðµ. ÐÐ±Ð½Ð¾Ð²Ð¸Ð» ÑÑÑÐ°Ð½Ð¸ÑÑ, Ð¿Ð¾ÐºÐ° ÐºÐ»Ð¸ÐµÐ½Ñ Ð¿Ð¾Ð´Ð¿Ð¸ÑÑÐ²Ð°ÐµÑ, â
        // Ð¸ Ð°Ð½ÐºÐµÑÑ Ð¿ÑÐ¸ÑÐ¾Ð´Ð¸Ð»Ð¾ÑÑ Ð·Ð°Ð±Ð¸Ð²Ð°ÑÑ Ð·Ð°Ð½Ð¾Ð²Ð¾.
        .then((result) => {
          if (shouldClearDraft(result)) removeDraft(draftStorageKey);
        })
        .finally(() => setLoading(false));
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  return (
    <div style={{ marginBottom: 50 }}>
      {/* <DealCreateDocs
        title="Ð¡Ð¾Ð³Ð»Ð°ÑÐ¸Ðµ Ð½Ð° Ð¾Ð±ÑÐ°Ð±Ð¾ÑÐºÑ Ð¿ÐµÑÑÐ¾Ð½Ð°Ð»ÑÐ½ÑÑ Ð´Ð°Ð½Ð½ÑÑ"
        deal={deal}
        documents={documents?.filter((item) => item?.typeId === DOCUMENT_TYPES_PERSONAL_DATA_AGREEMENT)}
        setDocumentsForm={setDocumentsForm}
        onSubmit={() => {}}
      /> */}
      <HookForm
        inputs={inputsWithDefaultValues || []}
        title="Ð£ÑÐ¾ÑÐ½ÐµÐ½Ð¸Ðµ Ð´Ð°Ð½Ð½ÑÑ ÐºÐ»Ð¸ÐµÐ½ÑÐ°"
        subtitle={shortFormsEnabled() ? undefined : () => {
          return (
            <div className={styles.checkboxGroup}>
              <Checkbox
                label="Ð¡ÑÐ±ÑÐ¸Ð´Ð¸Ñ"
                name="subsidy"
                id="subsidy"
                onChange={() => setSubsidy(!subsidy)}
                value={subsidy}
              />
              <Checkbox
              label="Ð Ð°ÑÑÑÐ¾ÑÐºÐ°"
              name="grace"
              id="grace"
              onChange={() => setGrace(!grace)}
              value={grace}
            />
            {/* <Checkbox
              label="ÐÐµÐ· Ð¿Ð¾Ð´ÑÐ²ÐµÑÐ¶Ð´ÐµÐ½Ð¸Ñ Ð´Ð¾ÑÐ¾Ð´Ð°"
              name="incomeConfirmation"
              id="incomeConfirmation"
              onChange={() => setincomeConfirmation(!incomeConfirmation)}
              value={incomeConfirmation}
            /> */}
              <Checkbox
                label="ÐÐµÑÐ²Ð¸ÑÐ½Ð°Ñ ÑÐµÐ³Ð¸ÑÑÑÐ°ÑÐ¸Ñ"
                name="vehicle_status"
                id="vehicle_status"
                onChange={() => setVehicle_status(!vehicle_status)}
                value={vehicle_status}
              />
              <Checkbox
                label="ÐÑÐ³Ð¾ÑÐ½Ð¾Ðµ ÐºÑÐµÐ´Ð¸ÑÐ¾Ð²Ð°Ð½Ð¸Ðµ"
                name="benefit"
                id="benefit"
                onChange={() => setBenefit(!benefit)}
                value={benefit}
              />
            </div>
          );
        }}
        grid={{ display: 'grid', gap: 20 }}
        onSubmit={handleSubmit}
        onValuesChange={persistDraft}
        loading={loading || sendFormLoading}
        submitDisabled={clientDocumentMissing}
        action={'Ð¡Ð¾ÑÑÐ°Ð½Ð¸ÑÑ Ð¸ Ð¿ÑÐ¾Ð´Ð¾Ð»Ð¶Ð¸ÑÑ'}
        customDataTop={
          clientDocumentMissing
            ? () => (
                <div
                  style={{
                    padding: 16,
                    borderRadius: 8,
                    background: '#FDECEC',
                    border: '1px solid #EE7171',
                  }}
                >
                  <p style={{ ...textStyles.fieldLabel, fontWeight: '400', color: '#C6280E' }}>
                    Ð£ ÐºÐ»Ð¸ÐµÐ½ÑÐ° Ð½Ðµ Ð·Ð°Ð¿Ð¾Ð»Ð½ÐµÐ½Ñ Ð´Ð°Ð½Ð½ÑÐµ ÑÐ´Ð¾ÑÑÐ¾Ð²ÐµÑÑÑÑÐµÐ³Ð¾ Ð´Ð¾ÐºÑÐ¼ÐµÐ½ÑÐ° â Ð½Ð¾Ð¼ÐµÑ, Ð´Ð°ÑÐ°
                    Ð²ÑÐ´Ð°ÑÐ¸ Ð¸ ÑÑÐ¾Ðº Ð´ÐµÐ¹ÑÑÐ²Ð¸Ñ. ÐÐµÐ· Ð½Ð¸Ñ Ð±Ð°Ð½ÐºÐ¸ Ð·Ð°ÑÐ²ÐºÑ Ð½Ðµ Ð¿ÑÐ¸Ð¼ÑÑ. ÐÐ°Ð¿Ð¾Ð»Ð½Ð¸ÑÐµ Ð¸Ñ Ð²
                    ÐºÐ°ÑÑÐ¾ÑÐºÐµ ÐºÐ»Ð¸ÐµÐ½ÑÐ° Ð¸ Ð²ÐµÑÐ½Ð¸ÑÐµÑÑ ÑÑÐ´Ð°.
                  </p>
                </div>
              )
            : undefined
        }
        customData={() => (
          <div
            style={{
              display: 'grid',
              gap: 20,
            }}
          >
            <p style={textStyles.formTitle}>
              Ð¡ÐºÐ°Ð½Ñ Ð´Ð¾ÐºÑÐ¼ÐµÐ½ÑÐ¾Ð²
              <Hint text="ÐÐµ Ð²ÑÐµ Ð±Ð°Ð½ÐºÐ¸ ÑÑÐµÐ±ÑÑÑ Ð¿ÑÐµÐ´Ð¾ÑÑÐ°Ð²Ð»ÐµÐ½Ð¸Ñ ÑÐºÐ°Ð½Ð¾Ð² Ð´Ð¾ÐºÑÐ¼ÐµÐ½ÑÐ¾Ð². ÐÑÐ»Ð¸ ÐºÐ»Ð¸ÐµÐ½ÑÑ Ð½ÐµÐ¾Ð±ÑÐ¾Ð´Ð¸Ð¼Ñ Ð¾ÑÐ²ÐµÑÑ Ð¾Ñ Ð²ÑÐµÑ Ð±Ð°Ð½ÐºÐ¾Ð², ÑÐµÐºÐ¾Ð¼ÐµÐ½Ð´ÑÐµÑÑÑ Ð·Ð°Ð³ÑÑÐ·Ð¸ÑÑ Ð²ÑÐµ Ð´Ð¾ÐºÑÐ¼ÐµÐ½ÑÑ." />
            </p>
            {/* <DocsInputNew
              text="ÐÐ¸ÑÑÐ¼Ð¾-Ð¿Ð¾Ð´ÑÐ²ÐµÑÐ¶Ð´ÐµÐ½Ð¸Ðµ Ð¿Ð¾ VVIN ÐºÐ¾Ð´"
              label="ÐÐ°Ð³ÑÑÐ·Ð¸ÑÑ Ð´Ð¾ÐºÑÐ¼ÐµÐ½Ñ"
              id="vinApprovalDocumentÑ"
              value={files['vinApprovalDocument']}
              onChange={(file) => attachFile(file, 'vinApprovalDocument')}
            /> */}

            <DocsInputNew
              text="Ð£ÐÐ"
              label="ÐÐ°Ð³ÑÑÐ·Ð¸ÑÑ Ð´Ð¾ÐºÑÐ¼ÐµÐ½Ñ"
              id="Ð£ÐÐ"
              value={files['UDV']}
              onChange={(file) => attachFile(file, 'UDV')}
              hint="Ð£ÐÐ Ð¸ ÐÐ°ÑÐ¿Ð¾ÑÑ Ð Ð Ð¾Ð±ÑÐ·Ð°ÑÐµÐ»ÑÐ½Ñ Ð´Ð»Ñ Bank CenterCredit Ð¸ Eurasian Bank. ÐÐ°Ð³ÑÑÐ·Ð¸ÑÐµ Ð¾Ð´Ð¸Ð½ Ð¸Ð· Ð´Ð¾ÐºÑÐ¼ÐµÐ½ÑÐ¾Ð² Ð´Ð»Ñ Ð¿Ð¾Ð´Ð°ÑÐ¸ Ð·Ð°ÑÐ²ÐºÐ¸ Ð² ÑÑÐ¸ Ð±Ð°Ð½ÐºÐ¸."
            />
            <DocsInputNew
              text="ÐÐ°ÑÐ¿Ð¾ÑÑ Ð Ð"
              label="ÐÐ°Ð³ÑÑÐ·Ð¸ÑÑ Ð´Ð¾ÐºÑÐ¼ÐµÐ½Ñ"
              id="ÐÐ°ÑÐ¿Ð¾ÑÑ Ð Ð"
              value={files['passportRK']}
              onChange={(file) => attachFile(file, 'passportRK')}
              hint="Ð£ÐÐ Ð¸ ÐÐ°ÑÐ¿Ð¾ÑÑ Ð Ð Ð¾Ð±ÑÐ·Ð°ÑÐµÐ»ÑÐ½Ñ Ð´Ð»Ñ Bank CenterCredit Ð¸ Eurasian Bank. ÐÐ°Ð³ÑÑÐ·Ð¸ÑÐµ Ð¾Ð´Ð¸Ð½ Ð¸Ð· Ð´Ð¾ÐºÑÐ¼ÐµÐ½ÑÐ¾Ð² Ð´Ð»Ñ Ð¿Ð¾Ð´Ð°ÑÐ¸ Ð·Ð°ÑÐ²ÐºÐ¸ Ð² ÑÑÐ¸ Ð±Ð°Ð½ÐºÐ¸."
            />
            {(inputsWithDefaultValues?.find((item) => item.name === 'IncomeAddConfirmed')?.value ||
              ['3', '6', '10']?.includes(inputsWithDefaultValues?.find((item) => item.name === 'workStatus')?.value?.value)) && (
              <DocsInputNew
                text="Ð¡Ð¿ÑÐ°Ð²ÐºÐ°/ÐÑÐ¿Ð¸ÑÐºÐ° Ð´Ð¾ÑÐ¾Ð´Ð°"
                label="ÐÐ°Ð³ÑÑÐ·Ð¸ÑÑ Ð´Ð¾ÐºÑÐ¼ÐµÐ½Ñ"
                id="Ð¡Ð¿ÑÐ°Ð²ÐºÐ°/ÐÑÐ¿Ð¸ÑÐºÐ° Ð´Ð¾ÑÐ¾Ð´Ð°"
                value={files['incomeCertificate']}
                onChange={(file) => attachFile(file, 'incomeCertificate')}
                hint="ÐÐ±ÑÐ·Ð°ÑÐµÐ»ÑÐ½ÑÐ¹ Ð´Ð¾ÐºÑÐ¼ÐµÐ½Ñ Ð´Ð»Ñ Ð¿Ð¾Ð´Ð°ÑÐ¸ Ð·Ð°ÑÐ²ÐºÐ¸ Ð² Eurasian Bank Ð¸ Bereke Bank."
              />
            )}

            {inputsWithDefaultValues?.find((item) => item.name === 'workStatus')?.value?.value == '9' && (
              <DocsInputNew
                text="ÐÑÐ¿Ð¸ÑÐºÐ° ÑÐ¾ ÑÑÐµÑÐ°"
                label="ÐÐ°Ð³ÑÑÐ·Ð¸ÑÑ Ð´Ð¾ÐºÑÐ¼ÐµÐ½Ñ"
                id="ÐÑÐ¿Ð¸ÑÐºÐ° ÑÐ¾ ÑÑÐµÑÐ°"
                value={files['workCertificate']}
                onChange={(file) => attachFile(file, 'workCertificate')}
              />
            )}
            <DocsInputNew
              text="Ð¤Ð¾ÑÐ¾Ð³ÑÐ°ÑÐ¸Ñ ÐºÐ»Ð¸ÐµÐ½ÑÐ°"
              label="ÐÐ°Ð³ÑÑÐ·Ð¸ÑÑ Ð´Ð¾ÐºÑÐ¼ÐµÐ½Ñ"
              id="Ð¤Ð¾ÑÐ¾Ð³ÑÐ°ÑÐ¸Ñ ÐºÐ»Ð¸ÐµÐ½ÑÐ°"
              value={files['clientPhoto']}
              onChange={(file) => attachFile(file, 'clientPhoto')}
              hint="ÐÐ±ÑÐ·Ð°ÑÐµÐ»ÑÐ½Ð¾ Ð´Ð»Ñ Bank CenterCredit Ð¸ Eurasian Bank"
            />

            {/* <DocsInputNew
              text="ÐÐ¾ÐºÑÐ¼ÐµÐ½Ñ Ð¿Ð¾Ð´ÑÐ²ÐµÑÐ¶Ð´Ð°ÑÑÐ¸Ð¹ ÐÐ"
              label="ÐÐ°Ð³ÑÑÐ·Ð¸ÑÑ Ð´Ð¾ÐºÑÐ¼ÐµÐ½Ñ"
              id="ÐÐ¾ÐºÑÐ¼ÐµÐ½Ñ Ð¿Ð¾Ð´ÑÐ²ÐµÑÐ¶Ð´Ð°ÑÑÐ¸Ð¹ ÐÐ"
              value={files['legalConfirm']}
              onChange={(file) => attachFile(file, 'legalConfirm')}
            /> */}

            <BanksPicker
              onChange={setSelectedBanks}
              defaultValue={selectedBanks}
              allowedBankIds={usedCar ? USED_CAR_BANK_IDS : undefined}
            />

            {!IS_PROD_STAND && (
              <div style={{ border: '1px dashed #F5C56B', background: '#FFF6E5', borderRadius: 8, padding: '12px 16px' }}>
                <Checkbox
                  label="Ð¢ÐÐ¡Ð¢: ÑÑÐ¸ÑÐ°ÑÑ Ð·Ð°ÑÐ²ÐºÑ Ð¿Ð¾Ð´Ð°Ð½Ð½Ð¾Ð¹ Ð¸Ð· Ð¼Ð¾Ð±Ð¸Ð»ÑÐ½Ð¾Ð³Ð¾ Ð¿ÑÐ¸Ð»Ð¾Ð¶ÐµÐ½Ð¸Ñ"
                  name="emulateMobileApp"
                  id="emulateMobileApp"
                  onChange={() => setEmulateMobileApp(!emulateMobileApp)}
                  value={emulateMobileApp}
                />
                <p style={{ fontSize: 11, color: '#8B8B8B', marginTop: 6 }}>
                  ÐÐ°ÑÐ²ÐºÐ° ÑÐ¹Ð´ÑÑ Ñ ÑÐ»Ð°Ð³Ð¾Ð¼ app=true, ÐºÐ°Ðº Ð¸Ð· Ð¿ÑÐ¸Ð»Ð¾Ð¶ÐµÐ½Ð¸Ñ: Ð±Ð°Ð½ÐºÐ¸ Ð²ÑÐ±ÐµÑÑÑÑÑ Ð°Ð²ÑÐ¾Ð¼Ð°ÑÐ¸ÑÐµÑÐºÐ¸ (ÑÐ¿Ð¸ÑÐ¾Ðº
                  Ð¼Ð¾Ð±Ð¸Ð»ÑÐ½Ð¾Ð³Ð¾ Ð¿ÑÐ¸Ð»Ð¾Ð¶ÐµÐ½Ð¸Ñ), Ð²ÑÐ±Ð¾Ñ Ð²ÑÑÐµ Ð±ÑÐ´ÐµÑ Ð¿ÑÐ¾Ð¸Ð³Ð½Ð¾ÑÐ¸ÑÐ¾Ð²Ð°Ð½. Ð§ÐµÑÐµÐ· 2 ÑÐ°ÑÐ° Ð±ÐµÐ· Ð¾ÑÐ²ÐµÑÐ° Ð±Ð°Ð½ÐºÐ° ÑÐ´ÐµÐ»ÐºÐ°
                  Ð·Ð°ÐºÑÐ¾ÐµÑÑÑ ÑÐ°Ð¼Ð°, Ð° Ð°Ð²ÑÐ¾Ð¼Ð¾Ð±Ð¸Ð»Ñ Ð²ÐµÑÐ½ÑÑÑÑ Ð² Ð½Ð°Ð»Ð¸ÑÐ¸Ðµ. Ð¢Ð¾Ð»ÑÐºÐ¾ Ð´Ð»Ñ ÑÐµÑÑÐ¾Ð²Ð¾Ð³Ð¾ ÑÑÐµÐ½Ð´Ð°.
                </p>
              </div>
            )}
          </div>
        )}
        backHandler={backHandler}
      />
      <Modal
        isOpen={agreementModal}
        onRequestClose={() => setAgreementModal(false)}
        width="100%"
        contentStyle={{
          maxWidth: 600,
          height: 'max-content',
        }}
      >
        <DealCreateAgreement
          deal={deal}
          // Ð¡Ð¾Ð³Ð»Ð°ÑÐ¸Ðµ Ð¿ÐµÑÐ°ÑÐ°ÐµÑÑÑ Ð´Ð¾ ÑÐ¾Ð·Ð´Ð°Ð½Ð¸Ñ Ð·Ð°ÑÐ²ÐºÐ¸ Ð² Ð±Ð°Ð½Ðº, Ð¿Ð¾ÑÑÐ¾Ð¼Ñ ÑÑÐ¼Ð¼Ñ Ð¸ ÑÑÐ¾ÐºÐ°
          // ÑÐ°Ð¼ Ð½ÐµÐ¾ÑÐºÑÐ´Ð° Ð²Ð·ÑÑÑ â Ð¿ÐµÑÐµÐ´Ð°ÑÐ¼ Ð¸Ñ Ð¸Ð· Ð°Ð½ÐºÐµÑÑ. ÐÐµÐ· ÑÑÐ¾Ð³Ð¾ Ð² Ð±Ð»Ð°Ð½ÐºÐµ Ð¤Ð¾ÑÑÐµ
          // Ð¿ÑÑÑÐ¾Ð²Ð°Ð»Ð¸ Â«Ð¡ÑÐ¼Ð¼Ð° Ð¸ Ð²Ð°Ð»ÑÑÐ° ÐºÑÐµÐ´Ð¸ÑÐ°Â», Â«Ð¡ÑÐ¾Ðº ÐºÑÐµÐ´Ð¸ÑÐ°Â», Â«Ð¡ÐµÐ¼ÐµÐ¹Ð½Ð¾Ðµ
          // Ð¿Ð¾Ð»Ð¾Ð¶ÐµÐ½Ð¸ÐµÂ» Ð¸ Â«ÐÐ¾Ð»Ð¸ÑÐµÑÑÐ²Ð¾ Ð½ÐµÑÐ¾Ð²ÐµÑÑÐµÐ½Ð½Ð¾Ð»ÐµÑÐ½Ð¸Ñ Ð´ÐµÑÐµÐ¹Â».
          creditAppeal={{
            loanMoney:
              deal?.sum && form?.initialFee
                ? deal.sum - Number(String(form.initialFee).replace(/\s/g, ''))
                : deal?.sum,
            // Ð form Ð»ÐµÐ¶Ð¸Ñ ÑÐ¶Ðµ Ð½Ð¾ÑÐ¼Ð°Ð»Ð¸Ð·Ð¾Ð²Ð°Ð½Ð½Ð°Ñ Ð°Ð½ÐºÐµÑÐ°, Ð³Ð´Ðµ period â ÑÐ¸ÑÐ»Ð¾,
            // Ð° Ð½Ðµ Ð²ÑÐ±ÑÐ°Ð½Ð½ÑÐ¹ Ð²Ð°ÑÐ¸Ð°Ð½Ñ ÑÐ¿Ð¸ÑÐºÐ°. ÐÐ¾ÑÑÐ¾Ð¼Ñ Ð±ÐµÑÑÐ¼ Ð¾Ð±Ð° Ð²Ð¸Ð´Ð°.
            period: form?.period?.value ?? form?.period,
            MaritalStatus: form?.MaritalStatus?.label || form?.MaritalStatus?.value,
            childrensCount: form?.childrensCount || 0,
          }}
          sendAppeal={() => onSubmit(form, documentsForm, files, true)}
          onClose={() => {
            setAgreementModal(false);
          }}
          sendFormLoading={sendFormLoading}
          bankIds={selectedBanks}
        />
      </Modal>
    </div>
  );
};

export default DealCreateCreditAppeal;
